import React, { useEffect, useState } from 'react';
import { supabase } from './lib/supabaseClient';
import { Branch } from './types/db';

interface PriceSimulatorProps {
  branches: Branch[];
  onRegisterClick: () => void;
}

export const PriceSimulator: React.FC<PriceSimulatorProps> = ({ branches, onRegisterClick }) => {
  const [prices, setPrices] = useState<any[]>([]);
  const [localBranches, setLocalBranches] = useState<Branch[]>([]);
  const [branch, setBranch] = useState('');
  const [program, setProgram] = useState('');
  const [kelas, setKelas] = useState('');
  const [result, setResult] = useState<any>(null);

  // Filter only active branches (either from props or loaded locally as fallback)
  const currentBranches = branches && branches.length > 0 ? branches : localBranches;
  const activeBranches = (currentBranches || []).filter((b) => b.is_active !== false);

  // format rupiah
  const format = (v: number) => new Intl.NumberFormat('id-ID').format(v);

  // ambil data
  useEffect(() => {
    const load = async () => {
      try {
        const [{ data: priceData }, { data: branchData }] = await Promise.all([
          supabase.from('program_prices').select('*'),
          supabase.from('branches').select('*'),
        ]);

        if (priceData) setPrices(priceData);
        if (branchData) setLocalBranches(branchData);
      } catch (err) {
        /* console removed */
      }
    };

    load();
  }, []);

  // hitung cicilan (JULI → MEI)
  const getRemainingMonths = () => {
    const month = new Date().getMonth() + 1;

    let remaining = month >= 7 ? 12 - month + 1 + 5 : 5 - month + 1;

    if (month === 6) remaining = 1;

    return remaining;
  };

  // AUTO HITUNG
  useEffect(() => {
    if (!branch || !program || !kelas) return;

    const found = prices.find(
      (p) =>
        p.branch_id === branch &&
        p.program_name?.toUpperCase() === program.toUpperCase() &&
        p.grade?.toUpperCase() === kelas.toUpperCase()
    );

    if (!found) {
      setResult(null);
      return;
    }

    try {
      // parsing data DB
      const durations = JSON.parse(found.installment_durations || '[]');

      const cash = Number(found.cash_price || 0);
      const installment = Number(found.installment_price || 0);
      const regis = Number(found.registration_fee || 0);

      const remainingMonths = getRemainingMonths();

      const tenor = durations.find((d: number) => d <= remainingMonths) || durations[0] || 1;

      const monthly = Math.ceil(installment / tenor);

      setResult({
        cash,
        installment,
        regis,
        tenor,
        monthly,
      });
    } catch (err) {
      /* console removed */
      setResult(null);
    }
  }, [branch, program, kelas, prices]);

  return (
    <section id="simulator" className="py-24 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-5xl mx-auto px-6">
        {/* TITLE */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-black uppercase tracking-tight text-gray-950">
            Simulasi Biaya Bimbel
          </h2>
          <p className="text-slate-500 mt-2 font-medium">
            Hitung biaya investasi belajar sesuai dengan unit cabang kamu
          </p>
        </div>

        {/* CARD */}
        <div className="bg-white p-8 md:p-12 rounded-[2.5rem] shadow-xl border border-slate-100 max-w-3xl mx-auto">
          {/* INPUT */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* CABANG */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-indigo-600 uppercase tracking-widest block ml-1">
                Cabang
              </label>
              <select
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                className="w-full border-2 border-slate-100 p-4 rounded-2xl font-bold bg-white text-xs outline-none focus:border-indigo-100 transition-all cursor-pointer"
              >
                <option value="">Pilih Cabang</option>
                {activeBranches.map((b) => (
                  <option key={b.id} value={b.id}>
                    📍 {b.name.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>

            {/* PROGRAM */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-indigo-600 uppercase tracking-widest block ml-1">
                Program
              </label>
              <select
                value={program}
                onChange={(e) => setProgram(e.target.value)}
                className="w-full border-2 border-slate-100 p-4 rounded-2xl font-bold bg-white text-xs outline-none focus:border-indigo-100 transition-all cursor-pointer"
              >
                <option value="">Pilih Program</option>
                <option value="REGULER">REGULER</option>
                <option value="TKAA">TKAA</option>
                <option value="UTBK">UTBK</option>
              </select>
            </div>

            {/* KELAS */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-indigo-600 uppercase tracking-widest block ml-1">
                Kelas
              </label>
              <select
                value={kelas}
                onChange={(e) => setKelas(e.target.value)}
                className="w-full border-2 border-slate-100 p-4 rounded-2xl font-bold bg-white text-xs outline-none focus:border-indigo-100 transition-all cursor-pointer"
              >
                <option value="">Pilih Kelas</option>
                <option value="5 SD">5 SD</option>
                <option value="6 SD">6 SD</option>
                <option value="7 SMP">7 SMP</option>
                <option value="8 SMP">8 SMP</option>
                <option value="9 SMP">9 SMP</option>
                <option value="10 SMA">10 SMA</option>
                <option value="11 SMA">11 SMA</option>
                <option value="12 SMA">12 SMA</option>
              </select>
            </div>
          </div>

          {/* RESULT */}
          {result && (
            <div className="mt-10 space-y-6">
              {/* CASH */}
              <div className="p-8 rounded-[2rem] bg-indigo-50/50 border border-indigo-100/50 text-center animate-fade-in">
                <p className="text-xs uppercase tracking-widest font-black text-slate-400">
                  Pembayaran Tunai (Cash)
                </p>
                <p className="text-4xl font-black text-indigo-600 mt-2">Rp {format(result.cash)}</p>
                <p className="text-xs text-indigo-400 font-bold mt-1">
                  + Biaya Pendaftaran Rp {format(result.regis)}
                </p>
                <p className="text-xs text-emerald-600 mt-3 font-black uppercase tracking-wider">
                  🔥 Lebih hemat dibanding cicilan
                </p>
              </div>

              {/* CICILAN */}
              <div className="p-8 rounded-[2rem] bg-slate-50 border border-slate-100/80 text-center animate-fade-in">
                <p className="text-xs uppercase tracking-widest font-black text-slate-400">
                  Estimasi Cicilan ({result.tenor} Bulan)
                </p>
                <p className="text-2xl font-extrabold text-slate-800 mt-2">
                  Rp {format(result.monthly)}{' '}
                  <span className="text-sm font-medium text-slate-400">/ bulan</span>
                </p>
                <p className="text-xs text-slate-400 font-bold mt-1">
                  + Biaya Pendaftaran Rp {format(result.regis)}
                </p>
              </div>

              {/* CTA */}
              <button
                onClick={onRegisterClick}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-5 rounded-[2rem] font-black uppercase tracking-widest text-xs hover:shadow-xl active:scale-[0.98] transition-all shadow-lg"
              >
                Daftar Online Sekarang 🚀
              </button>
            </div>
          )}

          {/* ERROR MESSAGE */}
          {!result && branch && program && kelas && (
            <div className="p-6 rounded-[2rem] bg-red-50 border border-red-100 text-center mt-8 animate-fade-in">
              <p className="text-xs font-black text-red-500 uppercase tracking-widest">
                Data Harga Belum Tersedia
              </p>
              <p className="text-[10px] text-red-400 mt-1 font-semibold">
                Silakan hubungi admin cabang terpilih untuk info ketersediaan program ini.
              </p>
            </div>
          )}

          {/* NOTE */}
          <p className="text-[10px] text-slate-400 mt-8 text-center leading-relaxed font-medium max-w-md mx-auto">
            *Biaya investasi pendidikan di tiap unit cabang dapat berbeda secara regional. Gunakan
            kalkulator simulasi ini untuk estimasi biaya pendaftaran terbaru.
          </p>
        </div>
      </div>
    </section>
  );
};
